import { Link } from '@tanstack/react-router'
import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { cn } from '@workspace/ui/lib/utils'
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  MessageSquareQuote,
} from 'lucide-react'
import { useState } from 'react'
import { Prose } from '../components/prose'
import { ConfidenceBar } from './components/confidence-bar'
import { EvaluationActions } from './components/evaluation-actions'
import { MetricBar } from './components/metric-bar'
import type { JudgeProfile } from './types'

function ExpandableFinding({
  icon,
  text,
  detail,
}: {
  icon: React.ReactNode
  text: string
  detail: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-white/[0.02]">
        {icon}
        <span className="flex-1 text-xs leading-relaxed text-foreground/80">{text}</span>
        <ChevronRight
          className={cn(
            'mt-0.5 size-3.5 shrink-0 text-muted-foreground/80 transition-transform',
            open && 'rotate-90'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pb-1.5 pl-9 pr-8">
          <Prose className="text-xs leading-relaxed text-muted-foreground">{detail}</Prose>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

const DEFAULT_VISIBLE_COUNT = 3

function FindingsList({
  title,
  items,
  icon,
}: {
  title: string
  items: JudgeProfile['strengths']
  icon: React.ReactNode
}) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? items : items.slice(0, DEFAULT_VISIBLE_COUNT)
  const hasHidden = items.length > DEFAULT_VISIBLE_COUNT

  return (
    <div className="rounded-xl border border-border bg-card/30">
      <div className="border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      <div className="px-2 py-2">
        {visible.map((f) => (
          <ExpandableFinding key={f.text} icon={icon} text={f.text} detail={f.detail} />
        ))}
        {hasHidden && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="mt-1 w-full rounded-lg px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground hover:bg-white/[0.02]"
          >
            Show all ({items.length})
          </button>
        )}
      </div>
    </div>
  )
}

export interface JudgeProfileViewProps {
  judge: JudgeProfile
  backTo?: string
}

export function JudgeProfileView({ judge, backTo = '/pitch-evaluation' }: JudgeProfileViewProps) {
  return (
    <div className="mx-auto max-w-6xl py-2">
      {/* Back link */}
      <Link
        to={backTo}
        className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3" />
        Back to review
      </Link>

      {/* Hero */}
      <div className="rounded-xl border border-border bg-card/30 p-6">
        <div className="flex items-start gap-6">
          <Avatar className="size-16">
            <AvatarFallback
              className="text-lg font-bold"
              style={{ backgroundColor: `${judge.color}20`, color: judge.color }}
            >
              {judge.initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-foreground">{judge.name}</h1>
              <span className="rounded-full bg-input px-2.5 py-0.5 text-xs text-muted-foreground">
                {judge.title}
              </span>
            </div>
            <p className="mt-1 text-xs italic text-muted-foreground/80">{judge.personality}</p>
            <Prose className="mt-3 text-sm leading-relaxed text-foreground/80">
              {judge.evaluationLens}
            </Prose>
          </div>
          <div className="shrink-0 text-right">
            <p
              className="text-sm font-semibold"
              style={{
                color: judge.verdict.toLowerCase().includes('pass') ? undefined : judge.color,
              }}
            >
              {judge.verdict}
            </p>
            {/* Confidence */}
            <ConfidenceBar
              value={judge.confidence}
              color={judge.color}
              className="mt-2"
              trackClassName="w-16"
            />
          </div>
        </div>
      </div>

      {/* Investment Thesis */}
      <div
        className="mt-4 rounded-xl border px-6 py-4"
        style={{ borderColor: `${judge.color}30`, backgroundColor: `${judge.color}08` }}
      >
        <div className="flex items-start gap-3">
          <MessageSquareQuote className="mt-0.5 size-4 shrink-0" style={{ color: judge.color }} />
          <Prose className="text-sm leading-relaxed text-foreground/80">
            {judge.investmentThesis}
          </Prose>
        </div>
      </div>

      {/* Metric Scores */}
      <div className="mt-6 rounded-xl border border-border bg-card/30 p-5">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Metric Scores</h2>
        <div className="space-y-1">
          {judge.metrics.map((m) => (
            <MetricBar key={m.label} label={m.label} score={m.score} reasoning={m.reasoning} />
          ))}
        </div>
      </div>

      {/* Strengths + Concerns */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <FindingsList
          title="What Stood Out"
          items={judge.strengths}
          icon={<CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-400" />}
        />
        <FindingsList
          title="Concerns Raised"
          items={judge.concerns}
          icon={<CircleAlert className="mt-0.5 size-3.5 shrink-0 text-amber-400" />}
        />
      </div>

      {/* Recommended Actions */}
      {judge.actions.length > 0 && <EvaluationActions actions={judge.actions} />}
    </div>
  )
}
